// v2 crab — a true sideways crab: a wide flattened carapace (broader across z
// than along x) over a darker rim skirt, mottled spots and little side spikes,
// eight two-segment legs that arch up at the knee then plant on the sand,
// stalked eyes, and the signature claws — jointed arms carrying one big and one
// smaller pincer hand, each with an open thumb-and-finger pinch. Two liveries:
// red shore crab and sandy ghost crab. Ported 1:1 from age-of-mcp's v2/crab.ts,
// using the library's shared `wildland` (tube) helper.
import * as THREE from "three";
import { defineModel, wildland, type Part } from "../../lib";

const LIVERY = [
  { shell: "#c84a2c", dark: "#a23a20", leg: "#a83e22", claw: "#d8583a" },
  { shell: "#c9a36a", dark: "#a8854e", leg: "#ab8852", claw: "#dcbb80" },
];

function crabParts(v: number): Part[] {
  const c = LIVERY[v % LIVERY.length]!;
  const parts: Part[] = [
    // carapace — wide across z — over a darker rim skirt
    { geometry: new THREE.IcosahedronGeometry(0.024, 0), color: c.shell,
      position: [0, 0.02, 0], scale: [1.15, 0.55, 1.6] },
    { geometry: new THREE.IcosahedronGeometry(0.02, 0), color: c.dark,
      position: [0, 0.015, 0], scale: [1.2, 0.42, 1.7] },
  ];
  // mottled spots on the shell top
  const spots: Array<[number, number, number]> = [
    [0.004, 0.0315, 0.012], [-0.006, 0.0315, -0.01], [0.008, 0.031, -0.014],
  ];
  for (const p of spots) {
    parts.push({ geometry: new THREE.IcosahedronGeometry(0.0045, 0),
      color: c.dark, position: [p[0], p[1], p[2]], scale: [1.2, 0.4, 1.2] });
  }
  for (const s of [1, -1] as const) {
    // side spike on the shell edge
    parts.push({ geometry: new THREE.ConeGeometry(0.004, 0.012, 3), color: c.dark,
      position: [0.004, 0.02, s * 0.042], rotation: [s * Math.PI / 2, 0, 0] });
    // four two-segment legs, arched up at the knee
    for (const hipX of [0.018, 0.006, -0.008, -0.02]) {
      parts.push(
        wildland.tube([hipX, 0.016, s * 0.03], [hipX - 0.002, 0.024, s * 0.052],
          0.0036, 0.0028, c.leg),
        wildland.tube([hipX - 0.002, 0.024, s * 0.052], [hipX - 0.005, 0, s * 0.067],
          0.0026, 0.0013, c.leg),
      );
    }
    // claw arm + hand (one side noticeably bigger) + open pinch
    const k = s > 0 ? 1.18 : 0.9;
    parts.push(
      wildland.tube([0.024, 0.014, s * 0.022], [0.046, 0.02, s * 0.034],
        0.004, 0.005, c.leg),
      { geometry: new THREE.IcosahedronGeometry(0.0115, 0), color: c.claw,
        position: [0.054, 0.021, s * 0.036],
        scale: [1.35 * k, 0.85 * k, 0.95 * k] },
      { geometry: new THREE.ConeGeometry(0.0035, 0.015, 3), color: c.claw,
        position: [0.067, 0.025, s * 0.035], rotation: [0, 0, -1.75] },
      { geometry: new THREE.ConeGeometry(0.0035, 0.015, 3), color: c.claw,
        position: [0.066, 0.017, s * 0.037], rotation: [0, 0, -1.35] },
    );
    // stalked eye
    parts.push(
      wildland.tube([0.03, 0.028, s * 0.009], [0.037, 0.042, s * 0.011],
        0.0018, 0.0018, c.dark),
      { geometry: new THREE.IcosahedronGeometry(0.0036, 0), color: "#14100c",
        position: [0.038, 0.0435, s * 0.011] },
    );
  }
  return parts;
}

export const crabV2 = defineModel({
  id: "crab-v2",
  batchId: "fable",
  name: "Crab (v2)",
  tags: ["creature","crab","low-poly","age-of-mcp","fable","water"],
  summary: "Low-poly crab.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.06,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => crabParts(0) },
    { id: "variant-1", label: "Variant 2", parts: () => crabParts(1) },
  ],
  animations: [],
});
